import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Popconfirm, 
  Modal, 
  Form, 
  DatePicker, 
  Switch,
  Card,
  Typography,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  LaptopOutlined
} from '@ant-design/icons';
import { 
  MentorWorkExperienceResponse, 
  CreateMentorWorkExperienceRequest, 
  UpdateMentorWorkExperienceRequest 
} from '../../../types';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;

const WorkExperienceTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWorkExperience, setEditingWorkExperience] = useState<MentorWorkExperienceResponse | null>(null);
  const [form] = Form.useForm();

  const { data: workExperiences = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.workExperiences,
    queryFn: async () => {
      const response = await apiService.getMentorWorkExperiences();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateMentorWorkExperienceRequest) => {
      const response = await apiService.createMentorWorkExperience(data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('İş deneyimi başarıyla eklendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.workExperiences });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMentorWorkExperienceRequest }) => {
      const response = await apiService.updateMentorWorkExperience(id, data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('İş deneyimi başarıyla güncellendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.workExperiences });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiService.deleteMentorWorkExperience(id);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      toast.success('İş deneyimi başarıyla silindi.');
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.workExperiences });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const handleAdd = () => {
    setEditingWorkExperience(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (workExperience: MentorWorkExperienceResponse) => {
    setEditingWorkExperience(workExperience);
    form.setFieldsValue({
      companyName: workExperience.companyName,
      position: workExperience.position,
      description: workExperience.description,
      startDate: workExperience.startDate ? dayjs(workExperience.startDate) : null,
      endDate: workExperience.endDate ? dayjs(workExperience.endDate) : null,
      isCurrentJob: workExperience.isCurrentJob,
      industry: workExperience.industry,
      achievements: workExperience.achievements
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const isCurrent = !!values.isCurrentJob;
      const workExperienceData = {
        ...values,
        startDate: values.startDate?.format('YYYY-MM-DD'),
        endDate: isCurrent ? null : (values.endDate ? values.endDate.format('YYYY-MM-DD') : null)
      };
      if (editingWorkExperience) {
        updateMutation.mutate({ id: editingWorkExperience.id, data: workExperienceData });
      } else {
        createMutation.mutate(workExperienceData);
      }
    } catch {
      toast.error('Form doğrulama hatası.');
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingWorkExperience(null);
    form.resetFields();
  };

  const filteredWorkExperiences = workExperiences.filter(workExperience =>
    workExperience.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
    workExperience.position.toLowerCase().includes(searchText.toLowerCase()) ||
    (workExperience.industry && workExperience.industry.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Şirket',
      dataIndex: 'companyName',
      key: 'companyName',
      sorter: (a: MentorWorkExperienceResponse, b: MentorWorkExperienceResponse) => 
        a.companyName.localeCompare(b.companyName),
    },
    {
      title: 'Pozisyon',
      dataIndex: 'position',
      key: 'position',
      sorter: (a: MentorWorkExperienceResponse, b: MentorWorkExperienceResponse) => 
        a.position.localeCompare(b.position),
    },
    {
      title: 'Sektör',
      dataIndex: 'industry',
      key: 'industry',
      sorter: (a: MentorWorkExperienceResponse, b: MentorWorkExperienceResponse) => 
        (a.industry || '').localeCompare(b.industry || ''),
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (a: MentorWorkExperienceResponse, b: MentorWorkExperienceResponse) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      render: (date: string) => dayjs(date).format('MM/YYYY'),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (a: MentorWorkExperienceResponse, b: MentorWorkExperienceResponse) => {
        if (!a.endDate && !b.endDate) return 0;
        if (!a.endDate) return 1;
        if (!b.endDate) return -1;
        return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
      },
      render: (date: string | null, record: MentorWorkExperienceResponse) => 
        (record.isCurrentJob || !date) ? 'Günümüz' : dayjs(date).format('MM/YYYY'),
    },
    {
      title: 'Mevcut İş',
      dataIndex: 'isCurrentJob',
      key: 'isCurrentJob',
      filters: [
        { text: 'Evet', value: true },
        { text: 'Hayır', value: false },
      ],
      onFilter: (value: boolean | React.Key, record: MentorWorkExperienceResponse) => record.isCurrentJob === value,
      render: (isCurrent: boolean) => isCurrent ? 'Evet' : 'Hayır',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorWorkExperienceResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ 
              backgroundColor: 'var(--text-secondary)', 
              color: 'white', 
              borderColor: 'var(--text-secondary)' 
            }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu iş deneyimini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              style={{ 
                backgroundColor: '#ef4444', 
                color: 'white', 
                borderColor: '#ef4444' 
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard
      title="İş Deneyimleri"
      description="İş deneyimlerinizi ekleyin veya düzenleyin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <TabListLoading loading={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
        </Col>
        <Col>
          <Space>
            <Search
              placeholder="Şirket, pozisyon veya sektör ara..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{ 
                backgroundColor: 'var(--text-secondary)', 
                borderColor: 'var(--text-secondary)' 
              }}
            >
              Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={filteredWorkExperiences}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />
      </TabListLoading>

      <Modal
        title={editingWorkExperience ? 'İş Deneyimi Düzenle' : 'Yeni İş Deneyimi'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okButtonProps={{
          style: { 
            backgroundColor: 'var(--text-secondary)', 
            borderColor: 'var(--text-secondary)',
            height: '32px',
            minWidth: '80px',
            fontWeight: 500,
            filter: 'none'
          }
        }}
        cancelButtonProps={{
          style: { 
            backgroundColor: 'white', 
            borderColor: '#d1d5db',
            height: '32px',
            minWidth: '80px',
            fontWeight: 500,
            filter: 'none'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ isCurrentJob: false }}
        >
          <Form.Item
            name="companyName"
            label="Şirket Adı"
            rules={[{ required: true, message: 'Şirket adı gereklidir' }]}
          >
            <Input placeholder="Şirket adını girin" />
          </Form.Item>

          <Form.Item
            name="position"
            label="Pozisyon"
            rules={[{ required: true, message: 'Pozisyon gereklidir' }]}
          >
            <Input placeholder="Pozisyonu girin" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea rows={3} placeholder="İş açıklaması" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="startDate"
              label="Başlangıç Tarihi"
              rules={[
                { required: true, message: 'Başlangıç tarihi gereklidir' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const today = dayjs().startOf('month');
                    const selectedDate = dayjs(value).startOf('month');
                    if (selectedDate.isAfter(today)) {
                      return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                    }
                    const endDate = form.getFieldValue('endDate');
                    if (endDate && !form.getFieldValue('isCurrentJob') && selectedDate.isAfter(dayjs(endDate).startOf('month'))) {
                      return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              style={{ flex: 1 }}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                picker="month"
                placeholder="Başlangıç tarihi"
                disabledDate={(current) => current && current.isAfter(dayjs(), 'month')}
              />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="Bitiş Tarihi"
              dependencies={['startDate', 'isCurrentJob']}
              style={{ flex: 1 }}
              rules={[
                {
                  validator: (_, value) => {
                    if (form.getFieldValue('isCurrentJob')) return Promise.resolve();
                    if (!value) return Promise.resolve();
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && dayjs(value).startOf('month').isBefore(dayjs(startDate).startOf('month'))) {
                      return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                picker="month"
                placeholder="Bitiş tarihi"
                disabled={form.getFieldValue('isCurrentJob')}
                disabledDate={(current) => {
                  if (form.getFieldValue('isCurrentJob')) return false;
                  const startDate = form.getFieldValue('startDate');
                  if (startDate && current) {
                    return current.isBefore(dayjs(startDate), 'month');
                  }
                  return false;
                }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="isCurrentJob"
            label="Mevcut İş"
            valuePropName="checked"
          >
            <Switch 
              onChange={(checked) => {
                if (checked) {
                  form.setFieldsValue({ endDate: null });
                }
              }}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="location"
              label="Konum"
              style={{ flex: 1 }}
            >
              <Input placeholder="Şehir/Ülke" />
            </Form.Item>

            <Form.Item
              name="industry"
              label="Sektör"
              style={{ flex: 1 }}
            >
              <Input placeholder="Sektör" />
            </Form.Item>
          </div>

          <Form.Item
            name="achievements"
            label="Başarılar"
          >
            <Input.TextArea rows={3} placeholder="Önemli başarılar ve katkılar" />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
    </>
  );
};

export default WorkExperienceTab;
